"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowUpRight } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { type FormSchemaType, formSchema } from "@/lib/form_schema";
import { submitForm } from "@/lib/form_submit";

export default function StartYourCampaign() {
	const [isSubmitting, setIsSubmitting] = useState(false);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<FormSchemaType>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			Name: "",
			Email: "",
			Phone: "",
			Message: "Booking a free consultation",
		},
	});

	const onSubmit = async (data: FormSchemaType) => {
		setIsSubmitting(true);
		try {
			const result = await submitForm(data);
			if (result.success) {
				toast.success("Request Sent.");
				reset();
			} else {
				toast.error(result.error || "Error sending request.");
			}
		} catch (_error) {
			toast.error("System Error.");
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<section className="mt-2 md:pt-8 pb-16 bg-transparent text-zinc-950 flex flex-col items-center justify-center font-sans">
			<div className="container mx-auto px-4 max-w-7xl">
				<form onSubmit={handleSubmit(onSubmit)} className="group w-full">
					<div className="relative flex w-full flex-col overflow-hidden rounded-3xl border border-zinc-200 bg-white text-zinc-950 shadow-2xl shadow-zinc-200/60 transition-shadow duration-300 hover:shadow-zinc-300/60 lg:flex-row">
						<div className="relative flex-1 overflow-hidden bg-zinc-50 p-5 md:p-8 lg:p-10">
							<div
								aria-hidden="true"
								className="pointer-events-none absolute -right-36 -bottom-px hidden h-80 w-[72%] opacity-[0.09] mix-blend-multiply lg:block xl:-right-44 xl:h-[360px] xl:w-[78%]"
							>
								<svg
									viewBox="0 0 560 292"
									aria-hidden="true"
									preserveAspectRatio="xMidYMax meet"
									className="h-full w-full drop-shadow-[0_18px_24px_rgba(150,0,8,0.16)]"
								>
									<defs>
										<linearGradient
											id="campaign-mountain-fill-a"
											x1="44"
											x2="526"
											y1="292"
											y2="58"
											gradientUnits="userSpaceOnUse"
										>
											<stop offset="0" stopColor="#f00512" stopOpacity="0.08" />
											<stop
												offset="0.58"
												stopColor="#f00512"
												stopOpacity="0.24"
											/>
											<stop offset="1" stopColor="#f00512" stopOpacity="0.44" />
										</linearGradient>
										<linearGradient
											id="campaign-mountain-fill-b"
											x1="104"
											x2="502"
											y1="292"
											y2="140"
											gradientUnits="userSpaceOnUse"
										>
											<stop offset="0" stopColor="#d3000b" stopOpacity="0.07" />
											<stop offset="1" stopColor="#d3000b" stopOpacity="0.3" />
										</linearGradient>
										<linearGradient
											id="campaign-mountain-fill-c"
											x1="164"
											x2="474"
											y1="292"
											y2="218"
											gradientUnits="userSpaceOnUse"
										>
											<stop offset="0" stopColor="#8e0007" stopOpacity="0.06" />
											<stop offset="1" stopColor="#8e0007" stopOpacity="0.2" />
										</linearGradient>
										<linearGradient
											id="campaign-mountain-line"
											x1="44"
											x2="526"
											y1="292"
											y2="58"
											gradientUnits="userSpaceOnUse"
										>
											<stop offset="0" stopColor="#ff8085" />
											<stop offset="0.56" stopColor="#f00512" />
											<stop offset="1" stopColor="#8e0007" />
										</linearGradient>
									</defs>
									<path
										d="M44 292l126-116 78 72 98-136 72 70 108-124v234H44Z"
										fill="url(#campaign-mountain-fill-a)"
									/>
									<path
										d="M104 292l88-76 70 42 92-88 64 44 84-74v152H104Z"
										fill="url(#campaign-mountain-fill-b)"
									/>
									<path
										d="M164 292l58-38 60 18 76-42 56 18 60-30v74H164Z"
										fill="url(#campaign-mountain-fill-c)"
									/>
									<path
										d="M44 292l126-116 78 72 98-136 72 70 108-124"
										fill="none"
										stroke="url(#campaign-mountain-line)"
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="13"
									/>
								</svg>
							</div>

							<div className="relative z-10">
								<h3
									aria-label="Video Ad Campaign Form"
									className="font-sans text-4xl md:text-6xl font-bold uppercase leading-none tracking-tight mb-6 md:mb-8"
								>
									Video Ad
									<br />
									<span className="text-red-600">Campaign Form</span>
								</h3>

								<div className="space-y-4 md:space-y-5">
									<div className="space-y-2">
										<label
											htmlFor="campaign-name"
											className="block text-sm font-semibold text-zinc-800"
										>
											Name <span className="text-zinc-400">(optional)</span>
										</label>
										<Input
											id="campaign-name"
											type="text"
											className="h-12 rounded-xl border-zinc-300 bg-white px-4 text-base font-medium shadow-sm shadow-zinc-200/70 transition-all placeholder:text-zinc-400 focus-visible:border-red-500 focus-visible:ring-2 focus-visible:ring-red-500/25 md:h-14"
											placeholder="Your name"
											{...register("Name")}
										/>
										{errors.Name && (
											<p className="mt-1.5 text-sm font-medium text-red-600">
												{errors.Name.message}
											</p>
										)}
									</div>

									<div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
										<div className="space-y-2">
											<label
												htmlFor="campaign-phone"
												className="block text-sm font-semibold text-zinc-800"
											>
												Phone <span className="text-zinc-400">(optional)</span>
											</label>
											<Input
												id="campaign-phone"
												type="tel"
												className="h-12 rounded-xl border-zinc-300 bg-white px-4 text-base font-medium shadow-sm shadow-zinc-200/70 transition-all placeholder:text-zinc-400 focus-visible:border-red-500 focus-visible:ring-2 focus-visible:ring-red-500/25 md:h-14"
												placeholder="Your phone number"
												{...register("Phone")}
											/>
											{errors.Phone && (
												<p className="mt-1.5 text-sm font-medium text-red-600">
													{errors.Phone.message}
												</p>
											)}
										</div>
										<div className="space-y-2">
											<label
												htmlFor="campaign-email"
												className="block text-sm font-semibold text-zinc-800"
											>
												Email <span className="text-red-600">*</span>
											</label>
											<Input
												id="campaign-email"
												type="email"
												required
												aria-invalid={Boolean(errors.Email)}
												className="h-12 rounded-xl border-zinc-300 bg-white px-4 text-base font-medium shadow-sm shadow-zinc-200/70 transition-all placeholder:text-zinc-400 focus-visible:border-red-500 focus-visible:ring-2 focus-visible:ring-red-500/25 md:h-14"
												placeholder="you@example.com"
												{...register("Email")}
											/>
											{errors.Email && (
												<p className="mt-1.5 text-sm font-medium text-red-600">
													{errors.Email.message}
												</p>
											)}
										</div>
									</div>

									<button
										type="submit"
										disabled={isSubmitting}
										className="flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-red-600 px-6 font-sans text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-red-600/20 transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-400 md:w-auto md:min-w-52"
									>
										{isSubmitting ? (
											<span className="size-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
										) : (
											<>
												Submit Request
												<ArrowUpRight className="size-5" />
											</>
										)}
									</button>
								</div>

								<div className="mt-6 md:mt-8 flex flex-wrap gap-2">
									{["REACH", "GROWTH", "EDITING", "ENGAGEMENT"].map((tag) => (
										<span
											key={tag}
											className="rounded-full border border-zinc-300 bg-white px-3 py-1 font-mono text-xs font-bold uppercase text-zinc-500"
										>
											{tag}
										</span>
									))}
								</div>
							</div>
						</div>

						<aside className="flex min-h-40 flex-col justify-between bg-red-600 p-5 text-white lg:w-56 lg:p-7">
							<div>
								<p className="font-mono text-xs font-bold uppercase tracking-widest text-white/70">
									Free Strategy
								</p>
								<p className="mt-3 text-2xl font-bold uppercase leading-tight">
									We reply within 24 hours.
								</p>
							</div>
							<ArrowUpRight className="mt-10 size-10" />
						</aside>
					</div>
				</form>

				<p className="text-center text-xs text-zinc-400 mt-4">
					Submit the form and we&apos;ll get back within 24 hours
				</p>
			</div>
		</section>
	);
}
